import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/common/SEOHead';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

type State = 'idle' | 'loading' | 'success' | 'error';

const UnsubscribeReviewInvitations: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const email = params.get('email');
  const [state, setState] = useState<State>('idle');
  const [message, setMessage] = useState<string>('');

  const handleConfirm = async () => {
    if (!token) {
      setState('error');
      setMessage('This unsubscribe link is missing its token. Please use the link from the email exactly as received.');
      return;
    }
    setState('loading');
    const { error } = await supabase.functions.invoke('manage-review-invitation', {
      body: { action: 'unsubscribe', token },
    });
    if (error) {
      setState('error');
      setMessage('We could not process this request. Please try again later.');
      return;
    }
    setState('success');
    setMessage(
      "You won't receive any further review invitations from PaperVerse at this address.",
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Unsubscribe from review invitations — PaperVerse"
        description="Opt out of future review invitations from PaperVerse authors."
        noindex
      />
      <Navbar />
      <main className="flex-1 container max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">
          Unsubscribe from review invitations
        </h1>
        <p className="text-muted-foreground mb-6">
          Confirm below to permanently opt out of future review invitations from PaperVerse authors
          {email ? ` at ${email}` : ''}.
        </p>
        <Card>
          <CardHeader>
            <CardTitle>Review invitations — opt out</CardTitle>
            <CardDescription>One click. Permanent for this email address.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {state === 'idle' && (
              <Button onClick={handleConfirm} disabled={!token}>
                Confirm unsubscribe
              </Button>
            )}
            {state === 'loading' && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing your request…
              </div>
            )}
            {state === 'success' && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
            {state === 'error' && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
            {state === 'idle' && !token && (
              <p className="text-sm text-destructive">
                This unsubscribe link is missing its token. Please use the link from the email exactly as received.
              </p>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default UnsubscribeReviewInvitations;
